import { useRef, useImperativeHandle, useCallback, useEffect } from "react";
import { useInputClassName } from "./useInputClassName.js";
function useNativeInput({
  // ControlStateProps
  active,
  disabled,
  loading,
  readOnly,
  required,
  focused,
  hovered,
  // ControlFocusProps
  onBlur,
  onFocus,
  onFocusChange,
  // ControlValueProps
  defaultValue,
  id,
  name,
  onChange,
  notNull,
  placeholder,
  type,
  value,
  // ControlConstraintProps
  max,
  maxLength,
  min,
  minLength,
  pattern,
  step,
  // ValidationStateProps
  onValidationStateChange,
  validationState,
  // ControlDisplayProps
  className: outerClassName,
  distinction,
  intent,
  scheme,
  size,
  style,
  // Common own props
  ...rest
}, forwardedRef) {
  const ref = useRef(null);
  useImperativeHandle(forwardedRef, () => ref.current);
  useChangeValidationState({ ref, onValidationStateChange });
  const onBlurListener = useCallback((event) => {
    if (event.defaultPrevented) {
      return;
    }
    onBlur?.();
    onFocusChange?.(false);
  }, [onBlur, onFocusChange]);
  const onFocusListener = useCallback((event) => {
    onFocus?.();
    onFocusChange?.(true);
  }, [onFocus, onFocusChange]);
  const className = useInputClassName({
    // ControlStateProps
    active,
    disabled,
    loading,
    readOnly,
    required,
    focused,
    hovered,
    // ControlDisplayProps
    className: outerClassName,
    distinction,
    intent,
    scheme,
    size,
    // ValidationStateProps
    validationState
  });
  return {
    ref,
    className,
    disabled: disabled || loading,
    id,
    name,
    onBlur: onBlurListener,
    onFocus: onFocusListener,
    placeholder: placeholder ?? void 0,
    readOnly: readOnly || loading,
    required,
    max: typeof max === "number" || typeof max === "string" ? max : void 0,
    maxLength,
    min: typeof min === "number" || typeof min === "string" ? min : void 0,
    minLength,
    pattern,
    step,
    style,
    ...rest
  };
}
const useChangeValidationState = ({ ref, onValidationStateChange }) => {
  const validationMessage = useRef();
  useEffect(() => {
    if (!(ref && typeof ref === "object" && onValidationStateChange)) {
      return;
    }
    const valid = ref.current?.validity?.valid;
    const message = valid ? void 0 : ref.current?.validationMessage;
    if (validationMessage.current !== message) {
      validationMessage.current = message;
      onValidationStateChange(message);
    }
  });
};
export {
  useChangeValidationState,
  useNativeInput
};
//# sourceMappingURL=useNativeInput.js.map
